import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  MessageCircle,
  Search,
  ChevronRight,
  ChevronDown,
  LayoutGrid,
  FileText,
  Wrench,
  Package,
  ArrowRight,
  Home,
  Building2,
  Tag,
} from 'lucide-react';
import { ThemeToggle } from '../components/ui/theme-toggle';
import { Button } from '../components/ui/button';
import { SocialNetworkIcon } from '../components/ui/social-icons';
import { usePdfViewer } from '../hooks/use-pdf-viewer';
import { useCatalog } from '../hooks/use-catalog';
import { useSettings } from '../hooks/settings/use-settings';
import { useSocialNetworks } from '../hooks/use-social-networks';
import { MegaMenu } from '../components/navigation/mega-menu';
import { CategoryEntity } from '../../domain/entities/category.entity';
import { ProductEntity } from '../../domain/entities/product.entity';

export const PublicLayout: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerSearch, setHeaderSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ProductEntity[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const mobileSearchContainerRef = useRef<HTMLDivElement>(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileCategoriesExpanded, setMobileCategoriesExpanded] = useState(false);
  const [categories, setCategories] = useState<CategoryEntity[]>([]);
  const [hasActivePromotions, setHasActivePromotions] = useState(false);
  const categoryTriggerRef = useRef<HTMLButtonElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { openPdf } = usePdfViewer();
  const { getCategoriesUseCase, getProductsUseCase, getPublicPromotionsUseCase } = useCatalog();
  const { contactInfo } = useSettings();
  const { networks: socialNetworks } = useSocialNetworks();

  const isPromoActive = location.pathname.startsWith('/catalogo') && new URLSearchParams(location.search).get('promocion') === 'true';
  const isCatalogActive = (location.pathname.startsWith('/catalogo') || location.pathname.startsWith('/productos')) && !isPromoActive;

  const activeSocialNetworks = (socialNetworks && socialNetworks.length > 0)
    ? socialNetworks.filter((sn) => sn.isActive).sort((a, b) => a.displayOrder - b.displayOrder)
    : [
      { id: 'fb', name: 'Facebook', type: 'FACEBOOK', url: 'https://facebook.com/bartech' },
      { id: 'ig', name: 'Instagram', type: 'INSTAGRAM', url: 'https://instagram.com/bartech' },
      { id: 'x', name: 'X (Twitter)', type: 'X', url: 'https://x.com/bartech' },
      { id: 'yt', name: 'YouTube', type: 'YOUTUBE', url: 'https://youtube.com/@bartech' },
    ];

  useEffect(() => {
    let isMounted = true;
    getCategoriesUseCase.execute().then((cats) => {
      if (isMounted) setCategories(cats);
    }).catch(console.error);
    return () => { isMounted = false; };
  }, [getCategoriesUseCase]);

  useEffect(() => {
    let isMounted = true;
    getPublicPromotionsUseCase.execute().then((promos) => {
      if (isMounted) {
        setHasActivePromotions(Array.isArray(promos) && promos.length > 0);
      }
    }).catch(() => {
      if (isMounted) setHasActivePromotions(false);
    });
    return () => { isMounted = false; };
  }, [getPublicPromotionsUseCase]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Bloquear scroll de la página cuando el menú móvil está abierto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Cerrar menú al cambiar de ruta o presionar Escape
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setMegaMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const query = headerSearch.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchTotal(0);
      setSearchDropdownOpen(false);
      return;
    }

    setSearchLoading(true);
    setSearchDropdownOpen(true);

    const timer = setTimeout(async () => {
      try {
        const res = await getProductsUseCase.executePaginated({
          search: query,
          limit: 5,
        });
        setSearchResults(res.products);
        setSearchTotal(res.total);
      } catch (err) {
        console.error('Error buscando productos:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [headerSearch, getProductsUseCase]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node) &&
        mobileSearchContainerRef.current &&
        !mobileSearchContainerRef.current.contains(e.target as Node)
      ) {
        setSearchDropdownOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (headerSearch.trim()) {
      setSearchDropdownOpen(false);
      navigate(`/catalogo?q=${encodeURIComponent(headerSearch.trim())}`);
    }
  };

  const renderSearchDropdown = () => {
    if (!searchDropdownOpen || headerSearch.trim().length < 2) return null;

    return (
      <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden z-50 animate-in fade-in-50 zoom-in-95 duration-150 text-left">
        {searchLoading ? (
          <div className="p-4 text-center text-xs text-neutral-500 dark:text-neutral-400 flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
            <span>Buscando equipos en catálogo...</span>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="p-5 text-center">
            <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">
              No se encontraron equipos para "{headerSearch}"
            </p>
            <p className="text-[11px] text-neutral-400 mt-1">
              Prueba buscando por marca (ej. Zebra, Honeywell, Barpos), modelo o características.
            </p>
          </div>
        ) : (
          <div>
            <div className="px-3.5 py-2 bg-neutral-50 dark:bg-neutral-800/60 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-[11px] text-neutral-500 font-semibold">
              <span>Sugerencias de productos ({searchResults.length} de {searchTotal})</span>
              <span className="text-[10px] text-neutral-400 hidden sm:inline">Presiona Enter para ver todos</span>
            </div>

            <div className="divide-y divide-neutral-100 dark:divide-neutral-800/80 max-h-[380px] overflow-y-auto">
              {searchResults.map((product) => {
                const brandLabel = (!product.brand || product.brand.toLowerCase() === 'false') ? 'BARTECH' : product.brand;
                return (
                  <Link
                    key={product.id}
                    to={`/catalogo/${product.slug}`}
                    onClick={() => {
                      setSearchDropdownOpen(false);
                      setHeaderSearch('');
                    }}
                    className="flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors group cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-white rounded-xl border border-neutral-200 dark:border-neutral-700 p-1 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-xs">
                      {product.images[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <Package className="w-5 h-5 text-neutral-400" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[10px] font-bold text-brand-primary uppercase tracking-wider">
                          {brandLabel}
                        </span>
                        {product.categoryName && (
                          <>
                            <span className="text-neutral-300 dark:text-neutral-600 text-[10px]">•</span>
                            <span className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                              {product.categoryName}
                            </span>
                          </>
                        )}
                      </div>
                      <h4 className="text-xs font-bold text-neutral-900 dark:text-white truncate group-hover:text-brand-primary transition-colors">
                        {product.name}
                      </h4>
                    </div>

                    <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </Link>
                );
              })}
            </div>

            {/* Botón inferior para ver todos los resultados */}
            <div className="p-2.5 bg-neutral-50 dark:bg-neutral-800/40 border-t border-neutral-100 dark:border-neutral-800">
              <button
                type="button"
                onClick={() => {
                  setSearchDropdownOpen(false);
                  navigate(`/catalogo?q=${encodeURIComponent(headerSearch.trim())}`);
                }}
                className="w-full py-2.5 px-3 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary/90 transition-colors flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
              >
                <span>Ver todos los resultados ({searchTotal} productos)</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-canvas text-content-primary transition-colors duration-200 relative">
      {/* Línea de Acento Tecnológico Heritage Bartech (#33CC00) */}
      <div className="h-[3.5px] w-full bg-[#33CC00] relative z-50 shadow-[0_0_12px_rgba(51,204,0,0.6)]" />

      {/* 1. Top Bar Corporativo Ejecutivo (Estilo Diprotec & pcFactory) - Visible en Tablet y Desktop */}
      <div className="hidden md:block bg-neutral-900 text-neutral-300 text-xs border-b border-neutral-800">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-col lg:flex-row items-center justify-between gap-2.5">
          {/* Lado Izquierdo: Redes Sociales & Contacto Directo */}
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-[11px] sm:text-xs">
            {/* Indicador de Estado Operativo */}
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-[#33CC00] bg-[#33CC00]/15 px-2.5 py-0.5 rounded-full border border-[#33CC00]/40 shadow-[0_0_8px_rgba(51,204,0,0.2)]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#33CC00] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#33CC00]"></span>
              </span>
              <span>Asesoría & Soporte Oficial</span>
            </span>
            {/* Redes Sociales Oficiales */}
            <div className="flex items-center gap-3 text-neutral-400 border-r border-neutral-700 pr-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 hidden sm:inline">
                Síguenos:
              </span>
              {activeSocialNetworks.map((sn) => (
                <a
                  key={sn.id}
                  href={sn.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${sn.name} BARTECH`}
                  className="hover:text-[#33CC00] transition-colors"
                  title={sn.name}
                >
                  <SocialNetworkIcon typeOrIcon={sn.type || sn.name} className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>

            {/* Datos de Contacto Directo */}
            <a
              href="mailto:ventas@bartech.cl"
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Mail className="w-3.5 h-3.5 text-brand-primary" />
              <span>ventas@bartech.cl</span>
            </a>

            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-brand-primary" />
              <span>+56 2 2923 8600</span>
              <span className="text-neutral-600">|</span>
              <a
                href="https://wa.me/56971380593"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                <span>+56 9 7138 0593</span>
              </a>
            </div>
          </div>

          {/* Lado Derecho: Dirección, Descarga de Presentación & Acceso */}
          <div className="flex items-center gap-4 text-[11px] sm:text-xs text-neutral-400">
            <div className="hidden xl:flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-neutral-500" />
              <span>Simón Bolívar 2389-A, Ñuñoa, Santiago</span>
            </div>

            <button
              type="button"
              onClick={() =>
                openPdf({
                  url: '/Presentacion-bartech.pdf',
                  title: 'Presentación Empresarial BARTECH & SmartServ',
                  subtitle: '19+ años de tecnología e ingeniería en hardware retail y POS',
                })
              }
              className="flex items-center gap-1.5 text-neutral-300 hover:text-white bg-neutral-800 hover:bg-neutral-700 px-2.5 py-1 rounded border border-neutral-700 transition-colors font-medium cursor-pointer"
              title="Visualizar Presentación Corporativa Oficial de BARTECH"
            >
              <FileText className="w-3.5 h-3.5 text-brand-primary" />
              <span>Ver Presentación (PDF)</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Main Navbar Ampliado con efecto Burbuja Flotante en Desktop */}
      <header
        className={`sticky z-40 transition-all duration-300 ease-out ${isScrolled
            ? 'top-0 md:top-3.5 md:px-4 lg:px-8 pointer-events-none'
            : 'top-0 px-0'
          }`}
      >
        <div
          className={`transition-all duration-300 ease-out mx-auto pointer-events-auto relative ${isScrolled
              ? 'bg-white/85 dark:bg-neutral-900/85 backdrop-blur-xl md:rounded-2xl lg:md:rounded-3xl border-b border-neutral-200/80 dark:border-neutral-800/80 md:border md:border-neutral-200/80 dark:md:border-neutral-700/60 md:shadow-xl md:shadow-neutral-950/10 dark:md:shadow-black/50 max-w-screen-2xl'
              : 'bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md border-b border-neutral-200/80 dark:border-neutral-800/80 rounded-none shadow-sm max-w-full'
            }`}
        >
          <div
            className={`max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-6 transition-all duration-300 ${isScrolled ? 'h-20 md:h-18 lg:h-19' : 'h-20 sm:h-22'
              }`}
          >
            {/* Logo Oficial BARTECH Expandido */}
            <Link to="/" className="flex items-center gap-3.5 group focus:outline-none flex-shrink-0">
              <div
                className={`rounded-xl bg-brand-primary flex items-center justify-center text-white font-extrabold shadow-md group-hover:scale-105 transition-all duration-300 ${isScrolled
                    ? 'w-11 h-11 md:w-10 md:h-10 text-2xl md:text-xl'
                    : 'w-11 h-11 sm:w-12 sm:h-12 text-2xl sm:text-3xl'
                  }`}
              >
                B
              </div>
              <div className="flex flex-col">
                <div
                  className={`flex items-baseline font-black tracking-tight leading-none transition-all duration-300 ${isScrolled ? 'text-2xl md:text-2xl' : 'text-2xl sm:text-3xl'
                    }`}
                >
                  <span className="text-brand-primary">BAR</span>
                  <span className="text-neutral-900 dark:text-white">TECH</span>
                </div>
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-neutral-500 dark:text-neutral-400 mt-1">
                  Tecnología e Ingeniería
                </span>
              </div>
            </Link>

            {/* Buscador Central Integrado (Estilo pcFactory & Diprotec) con Dropdown en Vivo */}
            <div ref={searchContainerRef} className="hidden lg:flex flex-1 max-w-lg relative">
              <form
                onSubmit={handleSearchSubmit}
                className="w-full relative"
              >
                <Search className="w-4 h-4 text-neutral-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={headerSearch}
                  onChange={(e) => setHeaderSearch(e.target.value)}
                  onFocus={() => {
                    if (headerSearch.trim().length >= 2) {
                      setSearchDropdownOpen(true);
                    }
                  }}
                  placeholder="Buscar modelos POS, lectores Honeywell, impresoras Zebra..."
                  className={`w-full pl-10 pr-20 bg-neutral-100/80 dark:bg-neutral-800/80 border border-neutral-300/80 dark:border-neutral-700/80 rounded-xl text-xs sm:text-sm placeholder-neutral-500 focus:outline-none focus:bg-white dark:focus:bg-neutral-900 focus-visible:ring-2 focus-visible:ring-brand-primary text-neutral-900 dark:text-white transition-all shadow-inner ${isScrolled ? 'py-2' : 'py-2.5'
                    }`}
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1 bg-neutral-900 dark:bg-neutral-700 hover:bg-brand-primary text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                >
                  Buscar
                </button>
              </form>
              {renderSearchDropdown()}
            </div>

            {/* Enlaces de Navegación Principales */}
            <nav className="hidden md:flex items-center gap-1 xl:gap-2">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-xl text-sm font-semibold transition-all inline-flex items-center gap-2 ${isActive
                    ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/40'
                    : 'text-neutral-700 dark:text-neutral-300 hover:text-brand-primary dark:hover:text-brand-primary hover:bg-neutral-100/70 dark:hover:bg-neutral-800/60'
                  }`
                }
              >
                <Home className="w-4 h-4 text-brand-primary" />
                <span>Inicio</span>
              </NavLink>

              {/* Botón Trigger de Mega Menu Categorías */}
              <button
                ref={categoryTriggerRef}
                type="button"
                onClick={() => {
                  setMegaMenuOpen((prev) => !prev);
                  if (categories.length === 0) {
                    getCategoriesUseCase
                      .execute()
                      .then((cats) => setCategories(cats))
                      .catch(console.error);
                  }
                }}
                className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-all inline-flex items-center gap-2 cursor-pointer ${
                  megaMenuOpen || isCatalogActive
                    ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/40'
                    : 'text-neutral-700 dark:text-neutral-300 hover:text-brand-primary dark:hover:text-brand-primary hover:bg-neutral-100/70 dark:hover:bg-neutral-800/60'
                }`}
                aria-expanded={megaMenuOpen}
                aria-haspopup="true"
              >
                <LayoutGrid className="w-4 h-4 text-brand-primary" />
                <span>Catálogo & Familias</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${megaMenuOpen ? 'rotate-180 text-brand-primary' : 'text-neutral-400'
                    }`}
                />
              </button>

              <NavLink
                to="/nosotros"
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-xl text-sm font-semibold transition-all inline-flex items-center gap-2 ${isActive
                    ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/40'
                    : 'text-neutral-700 dark:text-neutral-300 hover:text-brand-primary dark:hover:text-brand-primary hover:bg-neutral-100/70 dark:hover:bg-neutral-800/60'
                  }`
                }
              >
                <Building2 className="w-4 h-4 text-brand-primary" />
                <span>Nosotros & SmartServ</span>
              </NavLink>

              {/* Botón de Promociones (Solo visible si hay como mínimo 1 campaña activa) */}
              {hasActivePromotions && (
                <NavLink
                  to="/catalogo?promocion=true"
                  className={() =>
                    `px-3.5 py-2 rounded-xl text-sm font-semibold transition-all inline-flex items-center gap-2 ${isPromoActive
                      ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/40 font-bold'
                      : 'text-neutral-700 dark:text-neutral-300 hover:text-brand-primary dark:hover:text-brand-primary hover:bg-neutral-100/70 dark:hover:bg-neutral-800/60'
                    }`
                  }
                >
                  <Tag className="w-4 h-4 text-brand-primary" />
                  <span>Promociones</span>
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-primary"></span>
                  </span>
                </NavLink>
              )}
            </nav>

            {/* Acciones de la Derecha */}
            <div className="hidden sm:flex items-center gap-3">
              <ThemeToggle />

              <Link to="/contacto">
                <Button variant="primary" size="md" className="bg-[#33CC00] hover:bg-[#2EB800] text-white border-none shadow-[0_4px_14px_rgba(51,204,0,0.3)]">
                  <span>Cotizar / Contactar</span>
                </Button>
              </Link>
            </div>

            {/* Trigger Menú Móvil */}
            <div className="flex items-center gap-2 md:hidden">
              <ThemeToggle />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="p-2.5 rounded-xl text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#33CC00] cursor-pointer"
                aria-label="Abrir menú de navegación"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Mega Menú Flotante para Escritorio */}
          <MegaMenu
            isOpen={megaMenuOpen}
            onClose={() => setMegaMenuOpen(false)}
            categories={categories}
            triggerRef={categoryTriggerRef}
          />

          {/* Buscador en Mobile debajo del Header con Dropdown en Vivo */}
          <div ref={mobileSearchContainerRef} className="lg:hidden px-4 pb-3 relative">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="w-4 h-4 text-neutral-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={headerSearch}
                onChange={(e) => setHeaderSearch(e.target.value)}
                onFocus={() => {
                  if (headerSearch.trim().length >= 2) {
                    setSearchDropdownOpen(true);
                  }
                }}
                placeholder="Buscar modelos POS, lectores, impresoras..."
                className="w-full pl-10 pr-16 py-2 bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg text-xs placeholder-neutral-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#33CC00] text-neutral-900 dark:text-white"
              />
              <button
                type="submit"
                className="absolute right-1 top-1/2 -translate-y-1/2 px-2.5 py-1 bg-neutral-900 dark:bg-neutral-700 hover:bg-[#33CC00] text-white text-[11px] font-semibold rounded transition-colors cursor-pointer"
              >
                Buscar
              </button>
            </form>
            {renderSearchDropdown()}
          </div>

        </div>
      </header>

      {/* Backdrop Overlay para Menú Móvil */}
      <div
        className={`fixed inset-0 bg-neutral-950/70 backdrop-blur-sm z-[90] transition-opacity duration-300 md:hidden ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer Deslizable desde la Derecha (Fijo a toda la pantalla) */}
      <aside
        className={`fixed inset-y-0 right-0 z-[100] w-[85vw] max-w-xs sm:max-w-sm bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out transform md:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación principal"
      >
        {/* Cabecera del Drawer */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-primary flex items-center justify-center text-white font-black text-base shadow-sm">
              B
            </div>
            <div className="flex flex-col">
              <span className="font-black text-sm tracking-tight text-neutral-900 dark:text-white leading-tight">
                <span className="text-brand-primary">BAR</span>TECH
              </span>
              <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Tecnología & Hardware POS
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
              aria-label="Cerrar menú"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Contenido de Navegación con Scroll y min-h-0 */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2 bg-white dark:bg-neutral-900">
          <nav className="space-y-1.5">
            {/* 1. Inicio */}
            <NavLink
              to="/"
              end
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `group flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all ${isActive
                  ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/60 font-bold'
                  : 'text-neutral-900 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800/80'
                }`
              }
            >
              <div className="flex items-center gap-3">
                <Home className="w-4 h-4 text-neutral-500 dark:text-neutral-400 group-hover:text-brand-primary transition-colors" />
                <span>Inicio</span>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-200 transition-colors" />
            </NavLink>

            {/* 2. Catálogo de Hardware con Acordeón */}
            <div>
              <button
                type="button"
                onClick={() => setMobileCategoriesExpanded(!mobileCategoriesExpanded)}
                className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                  location.pathname.startsWith('/catalogo') || location.pathname.startsWith('/productos')
                    ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/60 font-bold'
                    : 'text-neutral-900 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <LayoutGrid className="w-4 h-4 text-brand-primary" />
                  <span>Catálogo de Hardware</span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-neutral-400 transition-transform duration-200 ${mobileCategoriesExpanded ? 'rotate-180 text-brand-primary' : ''
                    }`}
                />
              </button>

              {mobileCategoriesExpanded && (
                <div className="mt-1 mb-2 ml-4 pl-3 border-l-2 border-brand-primary/40 space-y-1.5 py-1">
                  <Link
                    to="/catalogo"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-1.5 text-xs font-bold text-brand-primary hover:underline py-1.5"
                  >
                    <span>Ver todo el catálogo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>

                  {categories.map((cat) => (
                    <div key={cat.id} className="space-y-0.5 pt-1">
                      <Link
                        to={`/catalogo?categoria=${cat.slug}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-xs font-semibold text-neutral-800 dark:text-neutral-200 hover:text-brand-primary flex items-center justify-between py-1"
                      >
                        <span>{cat.name}</span>
                        <span className="text-[10px] text-neutral-400 font-mono">
                          {cat.itemCount ?? 0}
                        </span>
                      </Link>
                      {cat.subcategories && cat.subcategories.length > 0 && (
                        <div className="pl-2.5 space-y-1 border-l border-neutral-200 dark:border-neutral-800 ml-1">
                          {cat.subcategories.map((sub) => (
                            <Link
                              key={sub.id}
                              to={`/catalogo?categoria=${cat.slug}&subcategoria=${sub.slug}`}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-[11px] text-neutral-500 dark:text-neutral-400 hover:text-brand-primary py-0.5"
                            >
                              • {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 3. Nosotros & SmartServ */}
            <NavLink
              to="/nosotros"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `group flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all ${isActive
                  ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/60 font-bold'
                  : 'text-neutral-900 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800/80'
                }`
              }
            >
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-neutral-500 dark:text-neutral-400 group-hover:text-brand-primary transition-colors" />
                <span>Nosotros & SmartServ</span>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-200 transition-colors" />
            </NavLink>

            {/* 4. Promociones (Solo visible si hay como mínimo 1 campaña activa) */}
            {hasActivePromotions && (
              <NavLink
                to="/catalogo?promocion=true"
                onClick={() => setMobileMenuOpen(false)}
                className={() =>
                  `group flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all ${isPromoActive
                    ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/60 font-bold'
                    : 'text-neutral-900 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800/80'
                  }`
                }
              >
                <div className="flex items-center gap-3">
                  <Tag className="w-4 h-4 text-brand-primary" />
                  <span>Promociones</span>
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-primary"></span>
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-200 transition-colors" />
              </NavLink>
            )}

            {/* 5. Contacto */}
            <NavLink
              to="/contacto"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `group flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all ${isActive
                  ? 'text-brand-primary bg-brand-50 dark:bg-brand-950/60 font-bold'
                  : 'text-neutral-900 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800/80'
                }`
              }
            >
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-neutral-500 dark:text-neutral-400 group-hover:text-brand-primary transition-colors" />
                <span>Contacto</span>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-200 transition-colors" />
            </NavLink>
          </nav>
        </div>

        {/* Pie del Drawer Minimalista */}
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/90 space-y-3 flex-shrink-0">
          {/* Redes Sociales Oficiales */}
          <div className="flex items-center justify-center gap-3 text-neutral-500 dark:text-neutral-400">
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">
              Síguenos:
            </span>
            <div className="flex items-center gap-2">
              {activeSocialNetworks.map((sn) => (
                <a
                  key={sn.id}
                  href={sn.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${sn.name} BARTECH`}
                  className="p-1.5 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-800 hover:text-brand-primary transition-colors"
                  title={sn.name}
                >
                  <SocialNetworkIcon typeOrIcon={sn.type || sn.name} className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Presentación Corporativa PDF */}
          <button
            type="button"
            onClick={() => {
              setMobileMenuOpen(false);
              openPdf({
                url: '/Presentacion-bartech.pdf',
                title: 'Presentación Empresarial BARTECH & SmartServ',
                subtitle: '19+ años de tecnología e ingeniería en hardware retail y POS',
              });
            }}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 font-semibold text-xs border border-neutral-200 dark:border-neutral-700 hover:border-brand-primary/50 transition-colors cursor-pointer shadow-xs"
          >
            <FileText className="w-3.5 h-3.5 text-brand-primary" />
            <span>Presentación Corporativa (PDF)</span>
          </button>
        </div>
      </aside>

      {/* Main Content Outlet */}
      <main className="flex-1 relative z-10">
        <Outlet />
      </main>

      {/* Botón Flotante Permanente de WhatsApp */}
      <a
        href="https://wa.me/56971380593?text=Hola%20BARTECH,%20quisiera%20consultar%20por%20equipamiento%20y%20hardware"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Atención directa por WhatsApp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white shadow-[0_4px_20px_rgba(37,211,102,0.4)] flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-400"
        title="Consultar con un especialista vía WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-[#33CC00]"></span>
        </span>
      </a>

      {/* Footer Tecnológico (Estilo Diprotec con Redes Sociales y SmartServ) */}
      <footer className="bg-neutral-900 text-neutral-400 border-t border-neutral-800 pt-16 pb-8 relative z-10">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12 pb-14 border-b border-neutral-800">
            {/* Columna Marca, Redes & Identidad */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-brand-primary flex items-center justify-center text-white font-black text-2xl">
                  B
                </div>
                <div className="flex flex-col">
                  <div className="flex items-baseline font-black tracking-tight text-2xl text-white">
                    <span className="text-brand-primary">BAR</span>TECH
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">
                    Tecnología e Ingeniería SpA
                  </span>
                </div>
              </div>

              <p className="text-sm text-neutral-400 max-w-sm leading-relaxed">
                Más de 19 años de experiencia en Chile. Importadores directos de equipamiento para puntos de venta, retail, captura de datos, automatización comercial y biometría.
              </p>

              {/* Redes Sociales en Footer */}
              <div className="pt-2">
                <span className="text-xs font-bold uppercase tracking-wider text-neutral-300 block mb-2.5">
                  Conéctate con BARTECH
                </span>
                <div className="flex items-center gap-3 text-neutral-400">
                  {activeSocialNetworks.map((sn) => (
                    <a
                      key={sn.id}
                      href={sn.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${sn.name} BARTECH`}
                      className="p-2 rounded-lg bg-neutral-800 hover:bg-brand-primary hover:text-white transition-colors"
                      title={sn.name}
                    >
                      <SocialNetworkIcon typeOrIcon={sn.type || sn.name} className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Soluciones POS & Retail */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">
                Líneas de Hardware
              </h4>
              <ul className="space-y-2.5 text-xs sm:text-sm">
                <li>
                  <Link to="/catalogo?categoria=pos" className="hover:text-white transition-colors">
                    Puntos de Venta (POS) All-in-One
                  </Link>
                </li>
                <li>
                  <Link to="/catalogo?categoria=lector-codigo-de-barras" className="hover:text-white transition-colors">
                    Lectores de Código de Barras 1D/2D
                  </Link>
                </li>
                <li>
                  <Link to="/catalogo?categoria=impresoras" className="hover:text-white transition-colors">
                    Impresoras Térmicas y de Etiquetas
                  </Link>
                </li>
                <li>
                  <Link to="/catalogo?categoria=capturadores-de-datos" className="hover:text-white transition-colors">
                    Capturadores de Datos (PDA Android)
                  </Link>
                </li>
                <li>
                  <Link to="/catalogo?categoria=banca" className="hover:text-white transition-colors">
                    Equipamiento Bancario y Gavetas
                  </Link>
                </li>
                <li>
                  <Link to="/catalogo?categoria=monitores" className="hover:text-white transition-colors">
                    Monitores y Pantallas Touch
                  </Link>
                </li>
              </ul>
            </div>

            {/* SmartServ - Servicio Técnico Oficial */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-1.5">
                <Wrench className="w-4 h-4 text-brand-primary" />
                SmartServ Oficial
              </h4>
              <ul className="space-y-2.5 text-xs sm:text-sm">
                <li>
                  <Link to="/nosotros#laboratorio" className="hover:text-white transition-colors">
                    Laboratorio Técnico en Santiago
                  </Link>
                </li>
                <li>
                  <Link to="/nosotros#cobertura" className="hover:text-white transition-colors">
                    Servicios de Roll-Out y Cobertura Nacional
                  </Link>
                </li>
                <li>
                  <Link to="/nosotros#garantia" className="hover:text-white transition-colors">
                    Mantención Preventiva y Garantías
                  </Link>
                </li>
                <li>
                  <Link to="/nosotros#homologacion" className="hover:text-white transition-colors">
                    Homologación de Drivers POS & SII
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() =>
                      openPdf({
                        url: '/Presentacion-bartech.pdf',
                        title: 'Presentación Empresarial BARTECH & SmartServ',
                        subtitle: 'Laboratorio técnico oficial y catálogo corporativo',
                      })
                    }
                    className="inline-flex items-center gap-1.5 text-brand-primary hover:underline font-semibold pt-1 cursor-pointer text-left"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span>Ver Presentación Institucional (PDF)</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Casa Matriz & Canales */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">
                Casa Matriz
              </h4>
              <div className="space-y-3 text-xs sm:text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                  <span>
                    {contactInfo?.addressStreet
                      ? `${contactInfo.addressStreet}, ${contactInfo.addressCity}`
                      : 'Simón Bolívar 2389-A, Ñuñoa, Santiago de Chile'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span>{contactInfo?.primaryPhone || '+56 2 2923 8600'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#25D366] flex-shrink-0" />
                  <span>{contactInfo?.whatsappNumber || '+56 9 7138 0593'} (WhatsApp)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span>{contactInfo?.salesEmail || 'ventas@bartech.cl'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-400">
            <p>© {new Date().getFullYear()} BARTECH Tecnología e Ingeniería SpA. Más de 19 años de excelencia e importación directa.</p>
            <div className="flex items-center gap-6">
              <Link to="/nosotros" className="hover:text-white transition-colors">
                Sobre Nosotros
              </Link>
              <Link to="/contacto" className="hover:text-white transition-colors">
                Asesoría de Proyectos
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
